import {Component, Inject, OnInit} from '@angular/core';
import {Config, ConfigType, Device} from 'smag-commons/cfg/connector';
import {FormBuilder, FormControl, FormGroup, Validators} from '@angular/forms';
import {MatSelectChange} from '@angular/material/select';
import {validateInteger, validateNotBlank, validateNotDuplicate} from '../../../commons/validators';
import {BACNET_DEFAULT_TTL, CONNECTOR_TYPES, METEO_SRC_TYPES} from '../connectors';
import {MAT_DIALOG_DATA} from '@angular/material/dialog';
import {
    MeteomaticsConfig,
    SourceConfig as MeteoSourceConfig,
    SourceType as MeteoSourceType,
    SourceType
} from 'smag-commons/cfg/connector/meteo';

export interface NewConnectorData {
    connectors: string[];
}

export interface NewConnector {
    id: string;
    config: Config<Device>;
}

@Component({
    selector: 'app-new-connector',
    templateUrl: './new-connector.component.html',
    styleUrls: ['./new-connector.component.scss']
})
export class NewConnectorComponent implements OnInit {

    form: FormGroup;
    cfg: Config<Device>;

    // exported in HTML
    CONNECTOR_TYPES = CONNECTOR_TYPES;
    ConfigType = ConfigType;
    METEO_SRC_TYPES = METEO_SRC_TYPES;
    SourceType = SourceType;

    constructor(@Inject(MAT_DIALOG_DATA) readonly data: NewConnectorData,
                private fb: FormBuilder) {
        this.form = fb.group({
            id: new FormControl('', [validateNotBlank, validateNotDuplicate(data.connectors)]),
            type: new FormControl(null, [Validators.required])
        });
        this.clearConfig();
    }

    ngOnInit() {
    }

    onSelectConnectorType(event: MatSelectChange) {
        this.clearConfig();
        this.cfg.type = event.value as ConfigType;
        // clear form controls and add connector-specific controls
        this.configureControls();
    }

    onMeteoSourceTypeChange(event: MatSelectChange) {
        // clear form controls and add source-specific controls
        Object.keys(this.form.controls)
            .filter(ctrl => ctrl !== 'id' && ctrl !== 'type' && ctrl !== 'srcType')
            .forEach(ctrl => this.form.removeControl(ctrl));
        switch (this.form.controls.srcType.value) {
            case SourceType.METEOMATICS:
                this.form.addControl('apiUrl', new FormControl(null, [validateNotBlank]));
                this.form.addControl('user', new FormControl(null, [validateNotBlank]));
                this.form.addControl('pass', new FormControl(null, [validateNotBlank]));
                break;
        }
        this.form.updateValueAndValidity();
    }

    close(): NewConnector {
        return {
            id: this.form.controls.id.value,
            config: {...this.cfg, ...this.additionalParams()}
        };
    }

    private clearConfig() {
        this.cfg = {
            type: null,
            devices: {}
        };
    }

    private configureControls() {
        Object.keys(this.form.controls)
            .filter(ctrl => ctrl !== 'id' && ctrl !== 'type')
            .forEach(ctrl => this.form.removeControl(ctrl));
        switch (this.cfg.type) {
            case ConfigType.BACNET:
                this.form.addControl('host', new FormControl(null, [validateNotBlank]));
                this.form.addControl('bbdm', new FormControl(null, []));
                this.form.addControl('ttl', new FormControl(BACNET_DEFAULT_TTL, [Validators.required, validateInteger, Validators.min(0)]));
                break;
            case ConfigType.METEO:
                this.form.addControl('srcType', new FormControl(null, [Validators.required]));
                break;
        }
    }

    private additionalParams(): { [param: string]: any } {
        switch (this.cfg.type) {
            case ConfigType.BACNET:
                return {
                    host: this.form.controls.host.value,
                    bbdm: this.form.controls.bbdm.value,
                    ttl: this.form.controls.ttl.value
                };
            case ConfigType.METEO:
                return {
                    src_cfg: this.mkMeteoSourceCfg()
                }
            default:
                return {};
        }
    }

    private mkMeteoSourceCfg(): MeteoSourceConfig {
        const srcCfg: MeteoSourceConfig = {
            type: this.form.controls.srcType.value
        };
        switch (this.form.controls.srcType.value) {
            case MeteoSourceType.METEOMATICS:
                (srcCfg as MeteomaticsConfig).api_url = this.form.controls.apiUrl.value;
                (srcCfg as MeteomaticsConfig).username = this.form.controls.user.value;
                (srcCfg as MeteomaticsConfig).password = this.form.controls.pass.value;
                break;
        }
        return srcCfg
    }
}
